/**
 * Runs one citation chase against OpenAlex from the browser.
 *
 * Three stages, each a handful of requests: look the seeds up (free singleton calls),
 * fetch the works they cite (up to 100 IDs per list call), and fetch the works that cite
 * them (up to two pages of 100 per seed). Nothing is sent but DOIs, OpenAlex IDs and
 * field names; no key is used.
 */

import { fetchJson, runPool } from "@/lib/tool-kit/reference-tools/network";
import {
  backwardHits,
  batchesOf,
  type ChaseHit,
  type ChaseWork,
  citedByUrl,
  FORWARD_CAP,
  type ForwardOrder,
  forwardPages,
  mergeHits,
  PAGE_SIZE,
  parseWork,
  parseWorkList,
  planBackward,
  type SeedLine,
  seedUrl,
  worksByIdUrl,
} from "@/lib/tool-kit/research-discovery/citation-chaser";

export interface SeedOutcome {
  line: SeedLine;
  status: "found" | "missing" | "failed" | "skipped";
  work?: ChaseWork;
}

export interface ChaseResult {
  seeds: SeedOutcome[];
  backward: ChaseHit[];
  forward: ChaseHit[];
  /** References the seeds cite in all, before the cap. */
  backwardTotal: number;
  /** True when there were more references than `BACKWARD_CAP`. */
  backwardCapped: boolean;
  /** Cited-by works OpenAlex counts across the seeds (a work citing two seeds counts twice). */
  forwardTotal: number;
  /** True when a seed has more citing works than were fetched. */
  forwardTruncated: boolean;
  /** Requests that failed after a retry; the lists are incomplete when above zero. */
  failedCalls: number;
}

type Progress = (done: number, total: number) => void;

type Task =
  | { kind: "back"; ids: string[] }
  | { kind: "fwd"; seed: ChaseWork; page: number };

async function lookUpSeeds(
  queued: readonly SeedLine[],
  signal: AbortSignal,
  tick: () => void
): Promise<SeedOutcome[] | null> {
  const outcomes = await runPool(
    queued,
    async (line): Promise<SeedOutcome> => {
      const response = await fetchJson<unknown>(seedUrl(line.doi ?? ""), {
        signal,
        retries: 1,
      });
      tick();
      if (!response.ok) {
        return {
          line,
          status: response.reason === "not-found" ? "missing" : "failed",
        };
      }
      const work = parseWork(response.value);
      return work
        ? { line, status: "found", work }
        : { line, status: "missing" };
    },
    { concurrency: 2, minIntervalMs: 120, signal }
  );
  return outcomes;
}

function tasksFor(
  seeds: readonly ChaseWork[],
  backwardIds: readonly string[]
): Task[] {
  const tasks: Task[] = batchesOf(backwardIds, PAGE_SIZE).map(
    (ids): Task => ({ kind: "back", ids })
  );
  for (const seed of seeds) {
    for (let page = 1; page <= forwardPages(seed.citedBy); page++) {
      tasks.push({ kind: "fwd", seed, page });
    }
  }
  return tasks;
}

export async function runChase(
  lines: readonly SeedLine[],
  order: ForwardOrder,
  signal: AbortSignal,
  onProgress: Progress
): Promise<ChaseResult | null> {
  const queued = lines.filter((line) => line.state === "queued" && line.doi);
  let done = 0;
  let total = queued.length;
  const tick = () => {
    done += 1;
    onProgress(done, total);
  };

  const looked = await lookUpSeeds(queued, signal, tick);
  if (looked === null) {
    return null;
  }
  const outcomes: SeedOutcome[] = lines.map(
    (line) =>
      looked.find((outcome) => outcome.line === line) ?? {
        line,
        status: "skipped",
      }
  );
  const seeds = looked.flatMap((outcome) =>
    outcome.work ? [outcome.work] : []
  );
  const plan = planBackward(seeds);
  const tasks = tasksFor(seeds, plan.ids);
  total += tasks.length;
  onProgress(done, total);

  const works: ChaseWork[] = [];
  const cited: { work: ChaseWork; seedId: string }[] = [];
  let failedCalls = 0;
  const finished = await runPool(
    tasks,
    async (task) => {
      const url =
        task.kind === "back"
          ? worksByIdUrl(task.ids)
          : citedByUrl(task.seed.id, order, task.page);
      const response = await fetchJson<unknown>(url, { signal, retries: 1 });
      tick();
      if (!response.ok) {
        failedCalls += 1;
        return;
      }
      const found = parseWorkList(response.value);
      if (task.kind === "back") {
        works.push(...found);
      } else {
        cited.push(...found.map((work) => ({ work, seedId: task.seed.id })));
      }
    },
    { concurrency: 2, minIntervalMs: 120, signal }
  );
  if (finished === null) {
    return null;
  }

  return {
    seeds: outcomes,
    backward: backwardHits(works, plan.links),
    forward: mergeHits(cited, new Set(seeds.map((seed) => seed.id))),
    backwardTotal: plan.total,
    backwardCapped: plan.total > plan.ids.length,
    forwardTotal: seeds.reduce((sum, seed) => sum + seed.citedBy, 0),
    forwardTruncated: seeds.some((seed) => seed.citedBy > FORWARD_CAP),
    failedCalls,
  };
}
